import { createFileRoute } from "@tanstack/react-router";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import {
	SettingsForm,
	type SettingsState,
} from "#/components/settings/SettingsForm";
import { useSignInModal } from "#/hooks/useSignInModal";
import { toast } from "#/lib/toast";

export const Route = createFileRoute("/settings")({
	validateSearch: (search: Record<string, unknown>) => ({
		reason:
			search.reason === "username-required"
				? ("username-required" as const)
				: undefined,
	}),
	component: SettingsPage,
});

function SettingsPage() {
	const { isLoading, isAuthenticated } = useConvexAuth();
	const { open } = useSignInModal();
	useEffect(() => {
		if (!isLoading && !isAuthenticated) open();
	}, [isLoading, isAuthenticated, open]);
	if (isLoading) return null;
	if (!isAuthenticated) return null;
	return <SettingsContent />;
}

function SettingsContent() {
	const { t } = useTranslation("settings");
	const { reason } = Route.useSearch();
	const saved = useQuery(api.users.getMySettings);
	const saveSettings = useMutation(api.users.saveSettings);
	const [isSaving, setIsSaving] = useState(false);
	const [usernameError, setUsernameError] = useState<string | undefined>(
		undefined,
	);

	const [form, setForm] = useState<SettingsState>({
		username: "",
		country: "FI",
		city: "",
		shipsTo: "domestic",
	});

	// Sync saved values into form once loaded
	const [synced, setSynced] = useState(false);
	if (saved !== undefined && !synced) {
		setSynced(true);
		setForm({
			username: saved?.username ?? "",
			country: saved?.country ?? "FI",
			city: saved?.city ?? "",
			shipsTo: saved?.shipsTo ?? "domestic",
		});
	}

	async function handleSave() {
		if (isSaving) return;
		setUsernameError(undefined);

		// Client-side validation for new usernames
		if (!saved?.username) {
			if (!form.username) {
				setUsernameError(t("form.usernameRequired"));
				return;
			}
			if (form.username.length < 3 || form.username.length > 30) {
				setUsernameError(t("form.usernameLength"));
				return;
			}
			if (!/^[a-zA-Z0-9_-]+$/.test(form.username)) {
				setUsernameError(t("form.usernameInvalidChars"));
				return;
			}
		}

		setIsSaving(true);
		try {
			await saveSettings({
				username: !saved?.username && form.username ? form.username : undefined,
				country: form.country || undefined,
				city: form.city || undefined,
				shipsTo: form.shipsTo,
			});
			toast.success(t("toasts.saved"));
		} catch (err) {
			if (err instanceof ConvexError && typeof err.data === "string") {
				if (err.data.includes("taken")) {
					setUsernameError(t("form.usernameTaken"));
				} else if (err.data.includes("characters")) {
					setUsernameError(t("form.usernameLength"));
				} else if (err.data.includes("letters")) {
					setUsernameError(t("form.usernameInvalidChars"));
				} else {
					toast.error(t("toasts.error"));
				}
			} else {
				toast.error(t("toasts.error"));
			}
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<div className="page-wrap py-12">
			<div className="mx-auto max-w-xl flex flex-col gap-10">
				<div>
					<p className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">
						{t("header.tag")}
					</p>
					<h1
						className="text-4xl font-bold text-foreground leading-tight"
						style={{ fontFamily: "Fraunces, serif" }}
					>
						{t("header.title")}
					</h1>
					<p className="mt-2 text-sm text-muted-foreground">
						{t("header.description")}
					</p>
				</div>

				{reason === "username-required" && (
					<div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
						{t("warning.usernameRequired")}
					</div>
				)}

				{saved === undefined ? null : (
					<SettingsForm
						value={form}
						onChange={(patch) => {
							if (patch.username !== undefined) setUsernameError(undefined);
							setForm((s) => ({ ...s, ...patch }));
						}}
						onSave={handleSave}
						isSaving={isSaving}
						usernameIsLocked={!!saved?.username}
						usernameError={usernameError}
					/>
				)}
			</div>
		</div>
	);
}
